/** 拨号 Tab — 复刻原来的键盘 + 状态 + 历史 + 麦克风警告 */
import { CheckOutlined, EditOutlined, PhoneFilled, PhoneOutlined } from '@ant-design/icons';
import { Button, Divider, Input, Space, Tag, Tooltip, Typography, message } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AuthUser } from '../../api/auth-provider.js';
import { getUserExtension, setUserExtension } from '../../api/sip-config.js';
import { type SipGlobalConfigSnapshot, type SipState, sipService } from './shared.js';

const KEYPAD: Array<{ k: string; sub?: string }> = [
  { k: '1' },
  { k: '2', sub: 'ABC' },
  { k: '3', sub: 'DEF' },
  { k: '4', sub: 'GHI' },
  { k: '5', sub: 'JKL' },
  { k: '6', sub: 'MNO' },
  { k: '7', sub: 'PQRS' },
  { k: '8', sub: 'TUV' },
  { k: '9', sub: 'WXYZ' },
  { k: '*' },
  { k: '0', sub: '+' },
  { k: '#' },
];

function regStatusTag(s: SipState) {
  switch (s.registration) {
    case 'registered':
      return <Tag color="green">已注册</Tag>;
    case 'connecting':
      return <Tag color="blue">连接中</Tag>;
    case 'failed':
      return <Tag color="red">注册失败</Tag>;
    case 'disconnected':
      return <Tag color="orange">已断开</Tag>;
    default:
      return <Tag>未注册</Tag>;
  }
}

function callStatusText(s: SipState): string {
  switch (s.call) {
    case 'calling':
      return `正在呼叫 ${s.remote ?? ''}`;
    case 'ringing':
      return `振铃中... ${s.remote ?? ''}`;
    case 'incoming':
      return `来电: ${s.remote ?? ''}`;
    case 'in-call':
      return `通话中: ${s.remote ?? ''}`;
    case 'ended':
      return '通话已结束';
    case 'failed':
      return s.errorMessage ? `失败: ${s.errorMessage}` : '通话失败';
    default:
      return '';
  }
}

function formatDuration(ms: number): string {
  const sec = Math.floor(ms / 1000);
  const mm = String(Math.floor(sec / 60)).padStart(2, '0');
  const ss = String(sec % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

interface Props {
  user: AuthUser | undefined;
  sipState: SipState;
  cfg: SipGlobalConfigSnapshot;
  prefilledTarget?: string;
}

export function DialTab({ user, sipState, cfg, prefilledTarget }: Props) {
  // 最近呼叫已迁入"通话记录" tab (后端 sip_call_log 持久化), 本 tab 不再维护本地历史
  const [target, setTarget] = useState(prefilledTarget ?? '');
  const [editingExt, setEditingExt] = useState(false);
  const [extDraft, setExtDraft] = useState('');
  const [micState, setMicState] = useState<'unknown' | 'granted' | 'denied' | 'prompt'>('unknown');
  const [, force] = useState(0);

  useEffect(() => {
    if (prefilledTarget) setTarget(prefilledTarget);
  }, [prefilledTarget]);

  const myExtension = useMemo(() => {
    if (!user?.id) return '';
    return getUserExtension(user.id);
  }, [user?.id, cfg]);

  useEffect(() => {
    if (!editingExt) setExtDraft(myExtension);
  }, [myExtension, editingExt]);

  useEffect(() => {
    if (sipState.call !== 'in-call') return;
    const t = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [sipState.call]);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.permissions) return;
    navigator.permissions
      .query({ name: 'microphone' as PermissionName })
      .then((s) => {
        setMicState(s.state as 'granted' | 'denied' | 'prompt');
        s.onchange = () => setMicState(s.state as 'granted' | 'denied' | 'prompt');
      })
      .catch(() => {});
  }, []);

  const duration =
    sipState.call === 'in-call' && sipState.startedAt
      ? formatDuration(Date.now() - sipState.startedAt)
      : null;

  const saveExtension = useCallback(() => {
    const v = extDraft.trim();
    if (!v) return message.warning('分机号不能为空');
    if (user?.id) {
      setUserExtension(user.id, v);
      setEditingExt(false);
      message.success(`分机号已切换为 ${v}, 正在重新注册...`);
    }
  }, [extDraft, user?.id]);

  const handleDial = useCallback(async () => {
    const v = target.trim();
    if (!v) return message.warning('请输入要拨打的号码');
    if (micState !== 'granted') {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((t) => t.stop());
        setMicState('granted');
      } catch (err) {
        setMicState('denied');
        const msg = err instanceof Error ? err.message : String(err);
        return message.error(`无法访问麦克风: ${msg}`);
      }
    }
    const result = sipService.dial(v);
    if (!result.ok) return message.error(result.reason ?? '拨号失败');
    // 通话结束后 sipService.subscribeCallEvents 会自动写入后端 sip_call_log,
    // "通话记录" tab 即可看到本次呼叫
  }, [target, micState]);

  const handleKeyPress = (k: string) => {
    if (sipState.call === 'in-call') sipService.sendDtmf(k);
    setTarget((t) => t + k);
  };

  const inActiveCall =
    sipState.call === 'calling' ||
    sipState.call === 'ringing' ||
    sipState.call === 'incoming' ||
    sipState.call === 'in-call';

  return (
    <div>
      <Space>
        <Typography.Text strong>SIP 状态</Typography.Text>
        {regStatusTag(sipState)}
      </Space>

      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Typography.Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
          我的分机:
        </Typography.Text>
        {editingExt ? (
          <Space.Compact size="small" style={{ flex: 1 }}>
            <Input
              size="small"
              autoFocus
              value={extDraft}
              onChange={(e) => setExtDraft(e.target.value)}
              onPressEnter={saveExtension}
              placeholder="例 89660031"
            />
            <Button size="small" type="primary" icon={<CheckOutlined />} onClick={saveExtension}>
              保存
            </Button>
            <Button
              size="small"
              onClick={() => {
                setExtDraft(myExtension);
                setEditingExt(false);
              }}
            >
              取消
            </Button>
          </Space.Compact>
        ) : (
          <>
            <Typography.Text strong style={{ fontFamily: 'monospace' }}>
              {myExtension}
            </Typography.Text>
            <Tooltip title="修改分机号">
              <Button
                size="small"
                type="text"
                icon={<EditOutlined />}
                onClick={() => {
                  setExtDraft(myExtension);
                  setEditingExt(true);
                }}
              />
            </Tooltip>
          </>
        )}
      </div>

      {sipState.registration === 'failed' && sipState.errorMessage && (
        <Typography.Text type="danger" style={{ display: 'block', fontSize: 12, marginTop: 6 }}>
          注册失败: {sipState.errorMessage}
        </Typography.Text>
      )}

      {/* 麦克风权限提示统一由全局 <MicPermissionGate /> 弹窗承担, 此处不再重复展示 */}

      <Divider style={{ margin: '12px 0' }} />

      <Input
        value={target}
        onChange={(e) => setTarget(e.target.value)}
        placeholder="目标号码或 SIP 地址"
        size="large"
        style={{ fontSize: 18, textAlign: 'center' }}
        onPressEnter={handleDial}
        suffix={
          target ? (
            <Button type="text" size="small" onClick={() => setTarget(target.slice(0, -1))}>
              ⌫
            </Button>
          ) : undefined
        }
      />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 6,
          marginTop: 10,
        }}
      >
        {KEYPAD.map((it) => (
          <Button
            key={it.k}
            block
            style={{
              height: 46,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
            onClick={() => handleKeyPress(it.k)}
          >
            <span style={{ fontSize: 16, fontWeight: 600, lineHeight: 1 }}>{it.k}</span>
            {it.sub && <span style={{ fontSize: 9, color: '#999', lineHeight: 1 }}>{it.sub}</span>}
          </Button>
        ))}
      </div>

      <div style={{ marginTop: 12 }}>
        {sipState.call === 'incoming' ? (
          <Space style={{ width: '100%' }}>
            <Button type="primary" icon={<PhoneFilled />} onClick={() => sipService.answer()} block>
              接听
            </Button>
            <Button
              danger
              icon={<PhoneOutlined rotate={135} />}
              onClick={() => sipService.hangup()}
              block
            >
              拒接
            </Button>
          </Space>
        ) : inActiveCall ? (
          <Button
            danger
            block
            icon={<PhoneOutlined rotate={135} />}
            onClick={() => sipService.hangup()}
          >
            挂断 {duration && `(${duration})`}
          </Button>
        ) : (
          <Button
            type="primary"
            block
            icon={<PhoneFilled />}
            disabled={sipState.registration !== 'registered'}
            onClick={handleDial}
          >
            拨打
          </Button>
        )}
      </div>

      {sipState.call !== 'idle' && (
        <Typography.Text
          style={{ display: 'block', textAlign: 'center', marginTop: 8, fontSize: 12 }}
          type={sipState.call === 'failed' ? 'danger' : 'secondary'}
        >
          {callStatusText(sipState)}
        </Typography.Text>
      )}
    </div>
  );
}
